/**
 * Graph node category color utilities using FTI Design System tokens.
 *
 * Provides a visually-distinct color palette for entity types,
 * sourced from DS seriesColors (9 distinct chart colors).
 */
import { seriesColors, semantic } from '@fti/design-system';

/**
 * DS-based node category palette.
 * Uses the DS seriesColors array (9 visually distinct chart colors)
 * supplemented with semantic colors for additional types.
 *
 * seriesColors order: Blue, Red, Green, Orange, Navy, Teal, Yellow, LightBlue, Gray
 */
const NODE_CATEGORY_PALETTE: readonly string[] = [
  seriesColors[0], // Blue (#0067B1) — Person
  seriesColors[1], // Red (#E1523D) — Company
  seriesColors[2], // Green (#1BB680) — Transaction
  seriesColors[3], // Orange (#FF8453) — Account
  seriesColors[4], // Navy (#003763) — Document
  seriesColors[5], // Teal (#00C9D4) — Location
  seriesColors[6], // Yellow (#FFCB4C) — additional type
  seriesColors[7], // Light Blue (#448FE3) — additional type
  seriesColors[8], // Gray (#AEAEAE) — additional type
  semantic.info.dark,     // Dark teal — additional type
  semantic.warning.dark,  // Dark amber — additional type
  semantic.success.dark,  // Dark green — additional type
] as const;

/** Well-known entity type → palette index mapping. */
const KNOWN_TYPE_MAP: Record<string, number> = {
  person: 0,
  company: 1,
  organization: 1,
  transaction: 2,
  account: 3,
  document: 4,
  location: 5,
  address: 5,
  country: 5,
};

/** Stable hash for unknown entity types to get consistent color assignment. */
function stableHash(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * Get the color for a given entity type.
 * Known types get fixed assignments; unknown types use stable hash.
 */
export function getNodeColor(entityType: string): string {
  const lower = entityType.toLowerCase();
  const knownIdx = KNOWN_TYPE_MAP[lower];
  if (knownIdx !== undefined) {
    return NODE_CATEGORY_PALETTE[knownIdx];
  }
  return NODE_CATEGORY_PALETTE[stableHash(lower) % NODE_CATEGORY_PALETTE.length];
}

/**
 * Get the full palette array for Cytoscape style functions.
 */
export function getNodeColorPalette(): string[] {
  return [...NODE_CATEGORY_PALETTE];
}

/**
 * Build a color map from a list of category values.
 * Known entity types get fixed colors; others are assigned from the palette.
 */
export function buildNodeColorMap(values: string[]): Map<string, string> {
  const map = new Map<string, string>();
  const unknowns: string[] = [];
  for (const val of values) {
    const lower = val.toLowerCase();
    const knownIdx = KNOWN_TYPE_MAP[lower];
    if (knownIdx !== undefined) {
      map.set(val, NODE_CATEGORY_PALETTE[knownIdx]);
    } else {
      unknowns.push(val);
    }
  }
  for (const val of unknowns) {
    if (!map.has(val)) {
      map.set(val, getNodeColor(val));
    }
  }
  return map;
}
